// The turn order (slice 12; design/16-turn-tracker.md, design/20-combatant-
// token-link-and-movement-lock.md, plan §2.6).
//
// ONE order, seen redacted. The encounter is part of the table's state: a
// list of combatants in turn order, the pointer, the round, whether a fight
// is running, and the DM's priority list for the stones. The turn maths is
// the website's `client/src/utils/initiative.js` ported, with the order and
// tie-breaks the system's (issue 1303; stamped on each sort): initiative
// highest or lowest first with the unrolled last, ties by the modifier and a
// roll-off where the system says so, then the name; advance wraps with the
// round and ticks every timed effect at the End of Round (an effect lasts in
// one of the system's units of time, stamped on it); stepping back moves the pointer and restores
// nothing — BACK IS NOT UNDO; sorting and hand reordering carry the pointer
// with whoever it pointed at.
//
// A combatant links to none, one or several tokens; a token to at most one
// combatant (design/20 §3.1). The link is the list of token ids on the
// combatant, kept through a deletion so an undelete restores it (§6.5). What
// a player sees of the order is DERIVED from the tokens: a combatant with a
// visible token has a stone, a hidden one has none — absent, not masked —
// and its numbers follow the tokens' sharing record (design/16 §4). A
// combatant with no token on the board is the DM's alone unless the DM
// marks it shown. Whether a player may move a token during a fight is read
// here too, by the server and by the browser's preview, from the same
// function.
import { parseEffectMods } from '../rules/modifiers.js';
import { parsePoisonSecond, parseEffectRetry } from '../rules/specials.js';
import { diceRange, isPlainDice, parseTableDice } from '../rules/system.js';
import { poolResult, throwPool } from '../dice/pool.js';
import { isAdvantageWord } from '../rules/advantage.js';
import { LEGACY_INITIATIVE_DICE, LEGACY_ROUND, LEGACY_SORT, legacyRolledTotal } from '../legacy/records.js';
import { numberedNames, splitNumbered } from './naming.js';
import { dealOrder } from './cards.js';
import { parseGrow } from './forms.js';
import { MONSTER_SHARES, canSee } from './objects.js';
import { POOL_LIMITS } from '../rules/pools.js';
import { normaliseWaiting, parseDelayBody, reduceDelay, waitingSeen } from './delay.js';
import { normaliseOpportunities, parseOpportunityBody, reduceOpportunity } from './opportunity.js';
import { heldSeen, normaliseHeld, parseReadyBody, reduceReady, resumeTurn } from './ready.js';
/**
 * The fog that applies to a read: the state's, when it carries one. Every server path passes the whole state, and the
 * whole state is passed on, so the scene's vision (issue 1144) hides an unseen combatant's tokens as the fog does.
 */
const scopeOf = (s) => (s.fog && s.grid ? (s.vision?.on ? s : { fog: s.fog, grid: s.grid }) : null);
/** The most extra attacks one effect gives (issue 1509). */
export const MAX_EXTRA_ATTACKS = 4;
/** The most passes a round lists: past it, a score (typed in the thousands) acts no more that round. */
export const MAX_PASSES = 50;
export const RULE_CHECKS = ['quiet', 'warn', 'warn_dm'];
export const emptyEncounter = () => ({ running: false, round: 1, activeIndex: 0, combatants: [], priority: [] });
export const ENCOUNTER_LIMITS = { combatants: 200, effects: 20, priority: 40, label: 40, tokens: 50, initDice: 20, scoreModifier: 100 };
/** The words the browser and the server both say (design/20 §4): never whose turn it is. */
export const NOT_YOUR_TURN = "It's not your turn.";
export const NOT_IN_FIGHT = "It isn't in this fight.";
/** A player with two tokens in a fight, on the wrong one (qa-runs 1253): the turn is still theirs, so it says which. */
export const turnOfOwnToken = (name) => `It's ${name}'s turn, so ${name} is the token that can move.`;
export const TURN_ENDED = 'Your turn ended.';
export const NO_ENCOUNTER = 'No encounter is running.';
/** Initiative by cards (issue 1177): the fight changed between the draw and the order (the review of 2026-10-10). */
export const DEAL_CHANGED = 'The fight changed while the cards were being dealt. Deal again.';
/** An End turn that named a turn no longer current (issue 992): somebody else ended it first. */
export const TURN_ALREADY_ENDED = 'That turn has already ended.';
/**
 * The current turn as this reading shows it: null when nothing runs, or (a player) when something unseen acts. In a
 * fight by passes (issue 1525) it names the pass too: one combatant may act in two phases one after the other.
 */
/** A Combat Phase's key (issue 1529): the round, the pass and who acts — the phase a combatant's recoil was fired in. */
export const turnKey = (t) => (t ? `${t.round}:${t.pass ?? 0}:${t.active}` : null);
const isTurnKey = (v) => typeof v === 'string' && /^\d{1,7}:\d{1,3}:[0-9a-f-]{36}$/i.test(v);
const isFiredPoints = (v) => typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= 10_000;
/** The recoil a combatant has fired in the phase under way (issue 1529): none once the phase has moved on. */
export const firedNow = (c, enc) => {
    const key = turnKey(currentTurn(enc));
    return key && c?.fired?.turn === key ? c.fired.points : 0;
};
export function currentTurn(enc) {
    if (!enc.running)
        return null;
    const active = 'activeIndex' in enc ? enc.combatants[enc.activeIndex]?.id ?? null : enc.active;
    if (active === null)
        return null;
    return enc.passes ? { round: Number(enc.round) || 1, active, pass: enc.passes.pass } : { round: Number(enc.round) || 1, active };
}
/** The End turn request for the turn this reading shows, or null when there is none to end. */
export function endTurn(enc) {
    const turn = currentTurn(enc);
    return turn ? { kind: 'encounter.next', turn } : null;
}
// ── The turn maths, ported (client/src/utils/initiative.js) ────────────────
/** The system's initiative dice thrown and summed: the dice from the ONE generator the server holds (design/15 §3). */
export function throwDice(dice, rng) {
    // Kept, exploding or counted dice (issue 1312): the throw's result, the kept total or the successes.
    if (!isPlainDice(dice))
        return poolResult(dice, throwPool(dice, rng));
    let n = 0;
    for (let i = 0; i < dice.count; i += 1)
        n += rng(dice.sides);
    return n;
}
/** The system's initiative dice plus the bonus. */
export function rollInitiative(bonus, rng, dice) {
    return throwDice(dice, rng) + (Number(bonus) || 0);
}
/** A combatant's score (issue 1525): its initiative and any change since; null unrolled. A d20 combatant's is its roll. */
export const scoreOf = (c) => (typeof c.initRoll === 'number' ? c.initRoll + (Number(c.scoreModifier) || 0) + (Number(c.woundModifier) || 0) : null);
export function compareInitiative(a, b, rule) {
    const rolled = (c) => typeof c.initRoll === 'number';
    if (rolled(a) !== rolled(b))
        return rolled(a) ? -1 : 1;
    // The score (issue 1525: the roll and any change since, which a d20 combatant never has).
    const sa = scoreOf(a);
    const sb = scoreOf(b);
    if (sa !== null && sb !== null && sa !== sb)
        return rule.order === 'lowest' ? sa - sb : sb - sa;
    // The higher score as rolled, before the change (issue 1525; `first_pass`).
    if (rule.firstPass && rolled(a) && a.initRoll !== b.initRoll)
        return rule.order === 'lowest' ? a.initRoll - b.initRoll : b.initRoll - a.initRoll;
    const bonusA = Number(a.initBonus) || 0;
    const bonusB = Number(b.initBonus) || 0;
    if (rule.modifier && bonusA !== bonusB)
        return bonusB - bonusA;
    const tieA = a.tie ?? Infinity;
    const tieB = b.tie ?? Infinity;
    if (tieA !== tieB)
        return tieA < tieB ? -1 : 1;
    return String(a.name || '').localeCompare(String(b.name || ''));
}
/** Sort by initiative by the rule; the pointer follows whoever it pointed at, so sorting never changes whose turn it is. */
/** The combatants named first, in the order given (initiative by cards, issue 1177), the rest after as they stood; whoever was acting still acts. */
export function orderByCards(enc, order) {
    const first = order.map((id) => enc.combatants.find((c) => c.id === id)).filter((c) => !!c);
    const sorted = [...first, ...enc.combatants.filter((c) => !order.includes(c.id))];
    const active = enc.combatants[enc.activeIndex];
    return { ...enc, combatants: sorted, activeIndex: active ? Math.max(0, sorted.findIndex((c) => c.id === active.id)) : 0 };
}
export function sortByInitiative(enc, rule, ties) {
    // A sort that carries the roll-off's places sets them, and clears any left from a tie that no longer is one.
    const combatants = ties
        ? enc.combatants.map((c) => { const { tie: _old, ...rest } = c; return typeof ties[c.id] === 'number' ? { ...rest, tie: ties[c.id] } : rest; })
        : enc.combatants;
    const active = enc.combatants[enc.activeIndex];
    const sorted = [...combatants].sort((a, b) => compareInitiative(a, b, rule));
    const activeIndex = active ? Math.max(0, sorted.findIndex((c) => c.id === active.id)) : 0;
    return { ...enc, combatants: sorted, activeIndex };
}
/** The groups of combatants still tied after the rule's earlier tie-breaks (the roll and, by the rule, the modifier): the ties a roll-off breaks. */
export function tiedGroups(enc, rule) {
    const groups = new Map();
    for (const c of enc.combatants) {
        if (typeof c.initRoll !== 'number')
            continue;
        const score = rule.firstPass ? `${scoreOf(c)} ${c.initRoll}` : `${scoreOf(c)}`;
        const key = rule.modifier ? `${score} ${Number(c.initBonus) || 0}` : score;
        groups.set(key, [...(groups.get(key) ?? []), c]);
    }
    return [...groups.values()].filter((g) => g.length > 1);
}
/**
 * The roll-off for every tie left (design/29 §7: "the tied creatures roll again between themselves"), for a system
 * whose tie-breaks include it, thrown by the server with `roll` (the system's initiative dice): highest first — or
 * lowest, by the system's order — and those still level roll again among themselves. A group whose members already
 * hold distinct places keeps them, so sorting again mid-fight never reshuffles a tie settled at the start.
 */
/** How many times a group still level throws again before it keeps the order it came in. */
export const ROLLOFF_ROUNDS = 20;
export function rollOffPlaces(enc, rule, roll) {
    const places = {};
    for (const group of tiedGroups(enc, rule)) {
        const held = group.map((c) => c.tie);
        if (held.every((t) => typeof t === 'number') && new Set(held).size === group.length) {
            [...group].sort((a, b) => a.tie - b.tie).forEach((c, i) => { places[c.id] = i; });
            continue;
        }
        const order = [];
        // Dice that cannot differ (the review of 2026-10-10: a success pool that always succeeds) tie forever: after
        // ROLLOFF_ROUNDS throws a group still level keeps the order it came in, so the roll-off always ends.
        const settle = (ids, round) => {
            if (ids.length === 1) {
                order.push(ids[0]);
                return;
            }
            if (round >= ROLLOFF_ROUNDS) {
                order.push(...ids);
                return;
            }
            const thrown = ids.map((id) => ({ id, d: roll() }));
            const levels = [...new Set(thrown.map((t) => t.d))].sort((a, b) => (rule.order === 'lowest' ? a - b : b - a));
            for (const level of levels)
                settle(thrown.filter((t) => t.d === level).map((t) => t.id), round + 1);
        };
        settle(group.map((c) => c.id), 0);
        order.forEach((id, i) => { places[id] = i; });
    }
    return places;
}
/** Rounds a duration lasts: its amount in its unit counted in rounds by their seconds, part of a round counting as one. */
export const roundsOf = (d) => Math.ceil((d.amount * d.unit.seconds) / d.round.seconds);
/**
 * A duration one round shorter, or null when it runs out. It stays in its own unit while a whole number of them is
 * left (ten rounds of a minute's effect is "1 minute"); otherwise it is counted in rounds.
 */
export function tickDuration(d) {
    const left = roundsOf(d) - 1;
    if (left <= 0)
        return null;
    const seconds = left * d.round.seconds;
    return seconds % d.unit.seconds === 0 ? { ...d, amount: seconds / d.unit.seconds } : { amount: left, unit: d.round, round: d.round };
}
/** Every timed effect loses a round; one running out drops off; an indefinite one is untouched. */
export function decrementEffects(combatants) {
    return combatants.map((c) => ({
        ...c,
        effects: c.effects.flatMap((e) => {
            if (!e.duration)
                return [e];
            const next = tickDuration(e.duration);
            return next ? [{ ...e, duration: next }] : [];
        }),
    }));
}
/** "Bless (3)" when timed in rounds, "Bless (2 minutes)" in a longer unit, "Bless" when indefinite: one rendering for every surface. */
export function effectBadge(e) {
    if (e.turns)
        return `${e.label} (${e.turns})`;
    const d = e.duration;
    if (!d)
        return e.label;
    if (d.unit.key === d.round.key)
        return `${e.label} (${d.amount})`;
    return `${e.label} (${d.amount} ${d.amount === 1 ? d.unit.label.toLowerCase() : d.unit.plural})`;
}
/**
 * The next combatant; past the last one, the End of Round: wrap, round up, every timed effect ticks. A round's throw
 * (issue 1525; `reroll: "each_round"`), stamped by the server on the End turn that ends the round, is applied as the new
 * round starts and the order sorted by it. A fight by passes walks its phases instead (`advancePhase`).
 */
export function advanceTurn(enc, reroll, dead) {
    return walkTurns(enc, reroll, dead).enc;
}
/**
 * The End turn passing over the dead (qa-runs 401): each step on whose combatant is in `dead` is taken again, and the
 * skipped are listed (the log's *Orc 2 is dead: turn skipped*). The round turns at most once: a round where everyone is
 * dead still ends, and the new one stops at its first. The round's throw goes with the step that turns it.
 */
export function walkTurns(enc, reroll, dead) {
    let out = stepTurn(enc, reroll);
    const skipped = [];
    if (!dead?.length || !enc.running)
        return { enc: out, skipped };
    const isDead = (e) => { const c = e.combatants[e.activeIndex]; return !!c && dead.includes(c.id); };
    const allDead = enc.combatants.every((c) => dead.includes(c.id));
    for (let n = 0; n < enc.combatants.length * (MAX_PASSES + 1) && isDead(out); n += 1) {
        if (out.round !== enc.round && allDead)
            break;
        skipped.push(out.combatants[out.activeIndex].id);
        const next = stepTurn(out, out.round === enc.round ? reroll : undefined);
        if (next.round !== out.round && out.round !== enc.round)
            break;
        out = next;
    }
    return { enc: out, skipped };
}
/** The turn ending: the active combatant's effects timed by its turns (`turns`) count it, and go when none is left. */
function countTurnEffects(enc) {
    const at = enc.combatants[enc.activeIndex];
    if (!at || !at.effects.some((e) => e.turns))
        return enc;
    const effects = at.effects.flatMap((e) => (!e.turns ? [e] : e.turns > 1 ? [{ ...e, turns: e.turns - 1 }] : []));
    return { ...enc, combatants: enc.combatants.map((c, i) => (i === enc.activeIndex ? { ...c, effects } : c)) };
}
function stepTurn(ending, reroll) {
    const enc = countTurnEffects(ending);
    if (enc.passes)
        return advancePhase(enc, reroll);
    if (enc.combatants.length === 0)
        return enc;
    const next = enc.activeIndex + 1;
    // A new Combat Turn (issue 1524): every pool that refreshes each round is full again.
    if (next >= enc.combatants.length) {
        const wrapped = { ...enc, round: (Number(enc.round) || 1) + 1, activeIndex: 0, combatants: refreshPools(decrementEffects(enc.combatants), 'round') };
        return endTurnEffects(reroll ? { ...applyRoundRoll(wrapped, reroll), activeIndex: 0 } : wrapped);
    }
    return endTurnEffects({ ...enc, activeIndex: next });
}
/** Whether a combatant has a phase in pass `p` (issue 1525): its score less `step` per earlier pass is above 0; one unrolled acts once, in the first. */
export function hasPhase(c, p, step) {
    const s = scoreOf(c);
    return s === null ? p === 1 : s - step * (p - 1) > 0;
}
/**
 * The next phase (issue 1525; docs/MRCS/shadowrun-3.md §5.3): the one acting now has taken its phase this pass; next is
 * the first in the order who has not and still has a phase in it — so a score lowered mid-pass re-sorts what is not yet
 * taken, and nobody acts twice in one pass. None left, the next pass; none in that, the round ends: every timed effect
 * ticks, every round pool fills, and the round's throw (when stamped) re-sorts the order. A phase starts with no actions.
 */
function advancePhase(enc, reroll) {
    const ps = enc.passes;
    const { turn: _turn, ...base } = enc;
    const active = enc.running ? enc.combatants[enc.activeIndex] : undefined;
    const acted = active && !ps.acted.includes(active.id) ? [...ps.acted, active.id] : ps.acted;
    const open = (e, p, done) => e.combatants.findIndex((c) => !done.includes(c.id) && hasPhase(c, p, ps.step));
    let i = open(base, ps.pass, acted);
    if (i >= 0)
        return endTurnEffects({ ...base, activeIndex: i, passes: { ...ps, acted } });
    i = ps.pass < MAX_PASSES ? open(base, ps.pass + 1, []) : -1;
    if (i >= 0)
        return endTurnEffects({ ...base, activeIndex: i, passes: { ...ps, pass: ps.pass + 1, acted: [] } });
    let next = { ...base, round: (Number(enc.round) || 1) + 1, combatants: refreshPools(decrementEffects(base.combatants), 'round'), passes: { ...ps, pass: 1, acted: [] } };
    if (reroll)
        next = applyRoundRoll(next, reroll);
    return endTurnEffects({ ...next, activeIndex: Math.max(0, open(next, 1, [])) });
}
/**
 * The phase before (issue 1525), BACK IS NOT UNDO: the pointer moves and nothing is restored — the last to take a phase
 * this pass takes it again; at a pass's start, the previous pass's last phase; at a round's start, the last phase of the
 * round before by today's scores (a score thrown since is not thrown back).
 */
function regressPhase(enc) {
    const ps = enc.passes;
    const { turn: _turn, ...base } = enc;
    const at = (id) => base.combatants.findIndex((c) => c.id === id);
    const acted = ps.acted.filter((id) => at(id) >= 0);
    if (acted.length)
        return { ...base, activeIndex: at(acted[acted.length - 1]), passes: { ...ps, acted: acted.slice(0, -1) } };
    const inPass = (p) => base.combatants.filter((c) => hasPhase(c, p, ps.step)).map((c) => c.id);
    if (ps.pass > 1) {
        const ids = inPass(ps.pass - 1);
        if (ids.length)
            return { ...base, activeIndex: at(ids[ids.length - 1]), passes: { ...ps, pass: ps.pass - 1, acted: ids.slice(0, -1) } };
    }
    let p = 1;
    while (p < MAX_PASSES && inPass(p + 1).length)
        p += 1;
    const ids = inPass(p);
    return { ...base, round: Math.max(1, (Number(enc.round) || 1) - 1), activeIndex: ids.length ? at(ids[ids.length - 1]) : Math.max(0, base.combatants.length - 1), passes: { ...ps, pass: p, acted: ids.slice(0, -1) } };
}
/** Whether the End turn now would end the round (issue 1525): the server throws the next round's initiative for it. */
export const endsRound = (enc, dead) => enc.running && enc.combatants.length > 0 && advanceTurn(enc, undefined, dead).round !== enc.round;
/**
 * The round's phases in order (issue 1525; §5.3): every pass from the first, each listing the combatants with a phase in
 * it — the passes before this one as taken; this one as its phases were taken, then the one acting, then the rest in
 * the order; the passes after as they stand now. Empty for a fight without passes.
 */
export function phasesOf(enc) {
    const ps = enc.passes;
    if (!ps)
        return [];
    const out = [];
    const active = enc.running ? enc.combatants[enc.activeIndex] : undefined;
    for (let p = 1; p <= MAX_PASSES; p += 1) {
        if (p < ps.pass) {
            for (const c of enc.combatants)
                if (hasPhase(c, p, ps.step))
                    out.push({ id: c.id, pass: p, done: true });
            continue;
        }
        if (p === ps.pass) {
            const acted = ps.acted.filter((id) => enc.combatants.some((c) => c.id === id));
            for (const id of acted)
                out.push({ id, pass: p, done: true });
            if (active && !acted.includes(active.id))
                out.push({ id: active.id, pass: p, done: false });
            for (const c of enc.combatants)
                if (c !== active && !acted.includes(c.id) && hasPhase(c, p, ps.step))
                    out.push({ id: c.id, pass: p, done: false });
            continue;
        }
        const here = enc.combatants.filter((c) => hasPhase(c, p, ps.step));
        if (!here.length)
            break;
        for (const c of here)
            out.push({ id: c.id, pass: p, done: false });
    }
    return out;
}
/** A combatant's score in a pass (issue 1525): its score less the step for each earlier pass; null unrolled. */
export const passScore = (c, pass, step) => {
    const s = scoreOf(c);
    return s === null ? null : s - step * (pass - 1);
};
/**
 * A combatant's initiative thrown by the system's rule (issue 1525): on a rated count its own number of dice, summed,
 * with the faces — or nothing, when its number is not known; otherwise the system's dice as before. No Rule of Six:
 * a rated count throws plain dice.
 */
export function throwInitiative(rule, c, rng) {
    if (!rule.rated)
        return { total: throwDice(rule.dice, rng) };
    const n = c.initDice;
    if (!n || n < 1)
        return null;
    const faces = Array.from({ length: Math.min(n, ENCOUNTER_LIMITS.initDice) }, () => rng(rule.dice.sides));
    return { total: faces.reduce((a, b) => a + b, 0), faces };
}
/** Each throw on its combatant: the dice's total plus the bonus; on a rated count the faces kept for the line, else none. */
export function applyThrows(enc, results) {
    let next = enc;
    for (const r of results) {
        next = patchIn(next, r.id, (c) => {
            const { initFaces: _old, ...rest } = c;
            return { ...rest, initRoll: r.total + (Number(c.initBonus) || 0), ...(r.faces ? { initFaces: [...r.faces] } : {}) };
        });
    }
    return next;
}
/** A round's throw applied (issue 1525): every score set, then the order sorted by the stamped rule and roll-off. */
export function applyRoundRoll(enc, r) {
    return sortByInitiative(applyThrows(enc, r.results), r.rule, r.ties);
}
/**
 * The round's throw the server stamps (issue 1525): every combatant whose dice are known, the roll-off for the ties the
 * new scores leave thrown with the system's own die (the old round's places are not kept), and the rule they sort by.
 */
export function roundRoll(enc, rule, rng) {
    const results = enc.combatants.flatMap((c) => { const t = throwInitiative(rule, c, rng); return t ? [{ id: c.id, ...t }] : []; });
    const sort = sortRuleOf(rule);
    const thrown = applyThrows({ ...enc, combatants: enc.combatants.map(({ tie: _tie, ...c }) => c) }, results);
    const ties = rule.ties.includes('roll_off') ? rollOffPlaces(thrown, sort, () => throwDice(rule.dice, rng)) : {};
    return { results, dice: { ...rule.dice }, ...(rule.rated ? { rated: true } : {}), ties, rule: sort };
}
/**
 * The working of a rated throw (issue 1525; §5.3): "Init: 5 + 2d6 (2, 6) = 13", and with a change to the score since,
 * "… = 13 − 2 = 11".
 */
export function initiativeLine(bonus, faces, sides, modifier = 0, wound = 0) {
    const total = faces.reduce((a, b) => a + b, 0) + bonus;
    const base = `Init: ${bonus} + ${faces.length}d${sides} (${faces.join(', ')}) = ${total}`;
    const typed = modifier ? `${base} ${modifier < 0 ? '−' : '+'} ${Math.abs(modifier)} = ${total + modifier}` : base;
    // The wounds' change (issue 1526), after the DM's: "… = 13 − 2 wounded = 11".
    return wound ? `${typed} ${wound < 0 ? '−' : '+'} ${Math.abs(wound)} wounded = ${total + modifier + wound}` : typed;
}
/** A combatant's number of initiative dice from a linked sheet (issue 1525): its `initiative_dice` field, within bounds; else null. */
export function sheetInitDice(sheet) {
    const n = sheet?.find((x) => x.purpose === 'initiative_dice');
    return n && Number.isInteger(n.value) && n.value >= 1 ? Math.min(ENCOUNTER_LIMITS.initDice, n.value) : null;
}
/** The effects that end as a turn starts (issue 1508): the newly active combatant's that last until its next turn. */
export function endTurnEffects(enc) {
    const at = enc.combatants[enc.activeIndex];
    if (!at || !enc.combatants.some((c, i) => c.effects.some((e) => e.once?.until === at.id || (i === enc.activeIndex && e.until === 'next_turn'))))
        return enc;
    return { ...enc, combatants: enc.combatants.map((c, i) => ({ ...c, effects: c.effects.filter((e) => e.once?.until !== at.id && !(i === enc.activeIndex && e.until === 'next_turn')) })) };
}
/** The previous combatant, wrapping to the last with the round down, never below 1. Expired effects are NOT restored. */
export function regressTurn(enc) {
    if (enc.passes)
        return regressPhase(enc);
    if (enc.combatants.length === 0)
        return enc;
    const prev = enc.activeIndex - 1;
    if (prev < 0)
        return { ...enc, round: Math.max(1, (Number(enc.round) || 1) - 1), activeIndex: enc.combatants.length - 1 };
    return { ...enc, activeIndex: prev };
}
/** Swap the combatant at `index` with its neighbour; the pointer tracks whoever it pointed at. A no-op at the edges. */
export function moveCombatant(enc, index, delta) {
    const target = index + delta;
    if (index < 0 || index >= enc.combatants.length)
        return enc;
    if (target < 0 || target >= enc.combatants.length)
        return enc;
    const next = [...enc.combatants];
    [next[index], next[target]] = [next[target], next[index]];
    let { activeIndex } = enc;
    if (activeIndex === index)
        activeIndex = target;
    else if (activeIndex === target)
        activeIndex = index;
    return { ...enc, combatants: next, activeIndex };
}
/**
 * Names for combatants added by hand, by the ONE numbering rule tokens use
 * (design/16 §7): a second "Goblin" beside a "Goblin" makes them Goblin 1
 * and Goblin 2; a third becomes Goblin 3. Returns the renames of existing
 * combatants that the rule requires, and the new name.
 */
export function nameForAdded(name, existing) {
    const { stem, n } = splitNumbered(name);
    if (n !== null) {
        // A numbered name keeps its number unless a combatant already has it (a preset saved with "Goblin 1" loaded beside
        // "Goblin 1"): then it takes the stem's next free number, so no two combatants share a name.
        const taken = existing.some((c) => c.name.trim() === name.trim());
        return { name: taken ? numberedNames(stem, existing.map((c) => c.name), 0).source : name.trim(), renames: [] };
    }
    const mates = existing.filter((c) => splitNumbered(c.name).stem === stem);
    if (mates.length === 0)
        return { name: name.trim(), renames: [] };
    const exact = mates.find((c) => splitNumbered(c.name).n === null);
    if (exact) {
        const r = numberedNames(exact.name, existing.map((c) => c.name), 1);
        return { name: r.copies[0], renames: [{ id: exact.id, name: r.source }] };
    }
    return { name: numberedNames(name, existing.map((c) => c.name), 0).source, renames: [] };
}
export const ENCOUNTER_KINDS = [
    'encounter.add', 'encounter.remove', 'encounter.set', 'encounter.roll', 'encounter.rolled', 'encounter.sort', 'encounter.deal', 'encounter.dealt', 'encounter.move',
    'encounter.link', 'encounter.effects', 'encounter.standing', 'encounter.priority', 'encounter.start', 'encounter.end', 'encounter.next', 'encounter.back', 'encounter.action',
    'encounter.pool', 'encounter.spend', 'encounter.refresh', 'encounter.wounds', 'encounter.fired',
    // delay.ts's DELAY_KINDS, written out: delay.ts imports this module, so its constant is not read at load time here.
    'encounter.delay', 'encounter.act', 'encounter.waitLost',
    // ready.ts's, the same way.
    'encounter.ready', 'encounter.fire', 'encounter.readyLost', 'encounter.opportunity',
];
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isUuid = (v) => typeof v === 'string' && UUID_RE.test(v);
const isName = (v) => typeof v === 'string' && v.trim().length >= 1 && v.length <= 120;
const isLabel = (v) => typeof v === 'string' && v.trim().length >= 1 && v.length <= ENCOUNTER_LIMITS.label;
const isInt = (v) => typeof v === 'number' && Number.isInteger(v) && Math.abs(v) <= 1_000_000;
const isIds = (v, max) => Array.isArray(v) && v.length >= 1 && v.length <= max && v.every(isUuid) && new Set(v).size === v.length;
const isTokens = (v) => Array.isArray(v) && v.length <= ENCOUNTER_LIMITS.tokens && v.every(isUuid) && new Set(v).size === v.length;
const UNIT_KEY_RE = /^[a-z][a-z0-9_]{0,62}$/;
function parseUnit(v) {
    const u = v;
    if (typeof u !== 'object' || u === null || typeof u.key !== 'string' || !UNIT_KEY_RE.test(u.key) || !isLabel(u.label) || !isLabel(u.plural))
        return null;
    return typeof u.seconds === 'number' && Number.isInteger(u.seconds) && u.seconds >= 1 && u.seconds <= 1e9 ? { key: u.key, label: u.label.trim(), plural: u.plural.trim(), seconds: u.seconds } : null;
}
/** A duration: a whole amount of a well-formed unit, and the round. Whether the unit is the system's is checked as it is committed. */
export function parseDuration(v) {
    const d = v;
    if (typeof d !== 'object' || d === null || !isInt(d.amount) || d.amount < 1)
        return null;
    const unit = parseUnit(d.unit);
    const round = parseUnit(d.round);
    return unit && round ? { amount: d.amount, unit, round } : null;
}
/** An effect from the wire or the log: exact shape or null. One stored with `rounds` (before issue 1303) lasts that many rounds of 3.5's. */
/** An effect's picture key (qa-runs#1440): a short lowercase name; the client draws only the ones in its own set. */
export const isEffectIcon = (v) => typeof v === 'string' && /^[a-z0-9-]{1,40}$/.test(v);
export function parseEffect(v) {
    if (typeof v !== 'object' || v === null)
        return null;
    const { id, label, rounds, duration, good, mods, until, rule, extra, turns, once, endsOnAttack, miss, ward, second, retry, images, temp, icon, grow, keen } = v;
    if (!isUuid(id) || !isLabel(label) || typeof good !== 'boolean')
        return null;
    let lasts = null;
    if (duration !== undefined && duration !== null) {
        lasts = parseDuration(duration);
        if (!lasts)
            return null;
    }
    else if (rounds !== null && rounds !== undefined) {
        if (!(isInt(rounds) && rounds > 0))
            return null;
        lasts = { amount: rounds, unit: { ...LEGACY_ROUND }, round: { ...LEGACY_ROUND } };
    }
    const parsed = mods === undefined ? null : parseEffectMods(mods);
    if (mods !== undefined && !parsed)
        return null;
    if (until !== undefined && until !== 'next_turn')
        return null;
    if (once !== undefined && !(typeof once === 'object' && once !== null && isUuid(once.until)))
        return null;
    if (turns !== undefined && !(isInt(turns) && turns >= 1 && turns <= 100))
        return null;
    if (endsOnAttack !== undefined && endsOnAttack !== true)
        return null;
    if (miss !== undefined && !(typeof miss === 'number' && Number.isInteger(miss) && miss >= 1 && miss <= 100))
        return null;
    const warded = ward;
    if (warded !== undefined && !(typeof warded === 'object' && warded !== null && typeof warded.amount === 'number' && Number.isInteger(warded.amount) && warded.amount >= 1 && warded.amount <= 1000 && typeof warded.left === 'number' && Number.isInteger(warded.left) && warded.left >= 0 && warded.left <= 100000 && Array.isArray(warded.bypass) && warded.bypass.length <= 8 && warded.bypass.every((w) => typeof w === 'string' && w.length >= 1 && w.length <= 40)))
        return null;
    const by = parseEffectRule(rule);
    if (rule !== undefined && !by)
        return null;
    const more = extra === undefined ? null : parseExtra(extra);
    if (extra !== undefined && !more)
        return null;
    const then = second === undefined ? null : parsePoisonSecond(second);
    if (second !== undefined && !then)
        return null;
    const again = retry === undefined ? null : parseEffectRetry(retry);
    if (retry !== undefined && !again)
        return null;
    if (images !== undefined && !(isInt(images) && images >= 1 && images <= 8))
        return null;
    if (temp !== undefined && !(isInt(temp) && temp >= 1 && temp <= 1000))
        return null;
    if (icon !== undefined && !isEffectIcon(icon))
        return null;
    const grown = grow === undefined ? null : parseGrow(grow);
    if (grow !== undefined && !grown)
        return null;
    if (keen !== undefined && keen !== true)
        return null;
    return { id, label: label.trim(), duration: lasts, good, ...(grown ? { grow: grown } : {}), ...(keen ? { keen: true } : {}), ...(typeof icon === 'string' ? { icon } : {}), ...(typeof images === 'number' ? { images } : {}), ...(typeof temp === 'number' ? { temp } : {}), ...(then ? { second: then } : {}), ...(again ? { retry: again } : {}), ...(parsed && parsed.length ? { mods: parsed } : {}), ...(until ? { until: 'next_turn' } : {}), ...(once ? { once: { until: once.until } } : {}), ...(turns !== undefined ? { turns: turns } : {}), ...(endsOnAttack ? { endsOnAttack: true } : {}), ...(miss !== undefined ? { miss: miss } : {}), ...(warded ? { ward: { amount: warded.amount, bypass: [...warded.bypass], left: warded.left } } : {}), ...(by ? { rule: by } : {}), ...(more ? { extra: more } : {}) };
}
/** An effect's extra attacks (issue 1509): 1 to MAX_EXTRA_ATTACKS left, melee or ranged only or either; else null. */
function parseExtra(v) {
    const x = v;
    if (typeof x !== 'object' || x === null || Array.isArray(x) || Object.keys(x).some((k) => k !== 'left' && k !== 'only'))
        return null;
    if (!isInt(x.left) || x.left < 1 || x.left > MAX_EXTRA_ATTACKS)
        return null;
    if (x.only !== undefined && x.only !== 'melee' && x.only !== 'ranged')
        return null;
    return { left: x.left, ...(x.only ? { only: x.only } : {}) };
}
const RULE_ENTRY_RE = /^[a-z][a-z0-9_]{0,62}$/;
/** The rule an effect was put on by (issue 1508): an entry's id and a record's uuid; else null. */
function parseEffectRule(v) {
    const r = v;
    if (typeof r !== 'object' || r === null || typeof r.entry !== 'string' || !RULE_ENTRY_RE.test(r.entry) || !isUuid(r.roll))
        return null;
    return { entry: r.entry, roll: r.roll };
}
/** An encounter body from the wire or the log: exact shape or null. */
export function parseEncounterBody(b) {
    if (b.kind === 'encounter.delay' || b.kind === 'encounter.act' || b.kind === 'encounter.waitLost')
        return parseDelayBody(b);
    if (b.kind === 'encounter.ready' || b.kind === 'encounter.fire' || b.kind === 'encounter.readyLost')
        return parseReadyBody(b);
    if (b.kind === 'encounter.opportunity')
        return parseOpportunityBody(b);
    switch (b.kind) {
        case 'encounter.add': {
            if (!Array.isArray(b.combatants) || b.combatants.length < 1 || b.combatants.length > ENCOUNTER_LIMITS.combatants)
                return null;
            const out = [];
            for (const c of b.combatants) {
                if (typeof c !== 'object' || c === null)
                    return null;
                const { id, name, initBonus, tokens, shown } = c;
                if (!isUuid(id) || !isName(name))
                    return null;
                if (initBonus !== undefined && !isInt(initBonus))
                    return null;
                if (tokens !== undefined && !isTokens(tokens))
                    return null;
                if (shown !== undefined && typeof shown !== 'boolean')
                    return null;
                // Added by hand (no tokens), a combatant is shown unless said otherwise; from the board, its tokens decide.
                out.push({ id, name: name.trim(), initBonus: initBonus ?? 0, tokens: tokens ? [...tokens] : [], shown: shown !== undefined ? shown : (tokens === undefined || tokens.length === 0) });
            }
            if (new Set(out.map((c) => c.id)).size !== out.length)
                return null;
            if (b.ask !== undefined && !(Array.isArray(b.ask) && b.ask.length >= 1 && b.ask.every((id) => out.some((c) => c.id === id)) && new Set(b.ask).size === b.ask.length))
                return null;
            return { kind: 'encounter.add', combatants: out, ...(Array.isArray(b.ask) ? { ask: [...b.ask] } : {}) };
        }
        case 'encounter.remove':
            return isIds(b.ids, ENCOUNTER_LIMITS.combatants) ? { kind: 'encounter.remove', ids: [...b.ids] } : null;
        case 'encounter.set': {
            if (!isUuid(b.id))
                return null;
            const out = { kind: 'encounter.set', id: b.id };
            if (b.name !== undefined) {
                if (!isName(b.name))
                    return null;
                out.name = b.name.trim();
            }
            if (b.initBonus !== undefined) {
                if (!isInt(b.initBonus))
                    return null;
                out.initBonus = b.initBonus;
            }
            if (b.initRoll !== undefined) {
                if (b.initRoll !== null && !isInt(b.initRoll))
                    return null;
                out.initRoll = b.initRoll;
            }
            if (b.shown !== undefined) {
                if (typeof b.shown !== 'boolean')
                    return null;
                out.shown = b.shown;
            }
            // Issue 1525: a combatant's number of initiative dice (null: not known), and the change to its score since the throw.
            if (b.initDice !== undefined) {
                if (b.initDice !== null && !(isInt(b.initDice) && b.initDice >= 1 && b.initDice <= ENCOUNTER_LIMITS.initDice))
                    return null;
                out.initDice = b.initDice;
            }
            if (b.scoreModifier !== undefined) {
                if (!isInt(b.scoreModifier) || Math.abs(b.scoreModifier) > ENCOUNTER_LIMITS.scoreModifier)
                    return null;
                out.scoreModifier = b.scoreModifier;
            }
            if (b.sort !== undefined) {
                const sort = parseStampedSort(b.sort);
                if (!sort)
                    return null;
                out.sort = sort;
            }
            return out;
        }
        case 'encounter.roll': {
            // Roll monsters (qa-runs 405): with players asked, nobody else need be thrown.
            if (b.ask !== undefined && !isIds(b.ask, ENCOUNTER_LIMITS.combatants))
                return null;
            const none = b.ask !== undefined && Array.isArray(b.ids) && b.ids.length === 0;
            if (!none && !isIds(b.ids, ENCOUNTER_LIMITS.combatants))
                return null;
            if (b.advantage !== undefined && !isAdvantageWord(b.advantage))
                return null;
            return { kind: 'encounter.roll', ids: none ? [] : [...b.ids], ...(b.ask !== undefined ? { ask: [...b.ask] } : {}), ...(isAdvantageWord(b.advantage) ? { advantage: b.advantage } : {}) };
        }
        case 'encounter.rolled': {
            if (b.ask !== undefined && !isIds(b.ask, ENCOUNTER_LIMITS.combatants))
                return null;
            const sort = b.sort === undefined ? null : parseStampedSort(b.sort);
            if (b.sort !== undefined && !sort)
                return null;
            const thrown = parseThrows(b, b.ask !== undefined);
            if (!thrown)
                return null;
            const { results, dice } = thrown;
            // The released homebrew layer whose initiative dice these were (issue 1310); a draft's is never stamped.
            if (b.by !== undefined && (typeof b.by !== 'string' || !b.by.trim() || b.by.length > 120))
                return null;
            // The system whose initiative dice the table borrowed (issue 1305), by name.
            if (b.borrowed !== undefined && (typeof b.borrowed !== 'string' || !b.borrowed.trim() || b.borrowed.length > 120))
                return null;
            return { kind: 'encounter.rolled', results, ...(dice ? { dice } : {}), ...(thrown.rated ? { rated: true } : {}), ...(typeof b.by === 'string' ? { by: b.by } : {}), ...(typeof b.borrowed === 'string' ? { borrowed: b.borrowed } : {}),
                ...(b.ask !== undefined ? { ask: [...b.ask] } : {}), ...(sort ? { sort } : {}) };
        }
        case 'encounter.deal': return isUuid(b.deck) ? { kind: 'encounter.deal', deck: b.deck } : null;
        case 'encounter.dealt': {
            if (!isUuid(b.deck) || !Array.isArray(b.cards) || b.cards.length < 1 || b.cards.length > ENCOUNTER_LIMITS.combatants)
                return null;
            const cards = [];
            for (const r of b.cards) {
                const x = (r ?? {});
                if (!isUuid(x.id) || typeof x.card !== 'string' || !x.card.trim() || x.card.length > 60 || (x.rank !== null && !isInt(x.rank)) || !isInt(x.index) || x.index < 0)
                    return null;
                cards.push({ id: x.id, card: x.card, rank: x.rank, index: x.index });
            }
            if (new Set(cards.map((c) => c.id)).size !== cards.length)
                return null;
            return { kind: 'encounter.dealt', deck: b.deck, cards };
        }
        case 'encounter.sort': {
            let rule;
            if (b.rule !== undefined) {
                const r = parseSortRule(b.rule);
                if (!r)
                    return null;
                rule = r;
            }
            if (b.ties === undefined)
                return { kind: 'encounter.sort', ...(rule ? { rule } : {}) };
            const ties = parseTies(b.ties);
            if (!ties)
                return null;
            return { kind: 'encounter.sort', ties, ...(rule ? { rule } : {}) };
        }
        case 'encounter.start': {
            // Passes (issue 1525) come with the rule they re-sort by, or not at all.
            if (b.passes === undefined && b.rule === undefined)
                return { kind: 'encounter.start' };
            const p = b.passes;
            const rule = parseSortRule(b.rule);
            if (typeof p !== 'object' || p === null || Object.keys(p).some((k) => k !== 'step') || !isInt(p.step) || p.step < 1 || p.step > 100 || !rule)
                return null;
            return { kind: 'encounter.start', passes: { step: p.step }, rule };
        }
        case 'encounter.end':
        case 'encounter.back':
            return { kind: b.kind };
        case 'encounter.next': {
            let reroll;
            if (b.reroll !== undefined) {
                const r = b.reroll;
                const thrown = typeof r === 'object' && r !== null ? parseThrows(r, true) : null;
                const ties = thrown ? parseTies(r.ties) : null;
                const rule = thrown ? parseSortRule(r.rule) : null;
                if (!thrown || !thrown.dice || !ties || !rule)
                    return null;
                reroll = { results: thrown.results, dice: thrown.dice, ...(thrown.rated ? { rated: true } : {}), ties, rule };
            }
            if (b.dead !== undefined && !(Array.isArray(b.dead) && b.dead.length <= 500 && b.dead.every(isUuid)))
                return null;
            const deadOf = Array.isArray(b.dead) && b.dead.length ? { dead: [...b.dead] } : {};
            if (b.turn === undefined || b.turn === null)
                return { kind: 'encounter.next', turn: null, ...(reroll ? { reroll } : {}), ...deadOf };
            if (typeof b.turn !== 'object')
                return null;
            const { round, active, pass } = b.turn;
            if (!isInt(round) || round < 1 || !isUuid(active))
                return null;
            if (pass !== undefined && (!isInt(pass) || pass < 1 || pass > MAX_PASSES))
                return null;
            return { kind: 'encounter.next', turn: { round, active, ...(pass !== undefined ? { pass: pass } : {}) }, ...(reroll ? { reroll } : {}), ...deadOf };
        }
        case 'encounter.move':
            return isUuid(b.id) && (b.delta === -1 || b.delta === 1) ? { kind: 'encounter.move', id: b.id, delta: b.delta } : null;
        case 'encounter.link':
            return isUuid(b.id) && isTokens(b.tokens) ? {
                kind: 'encounter.link', id: b.id, tokens: [...b.tokens], ...(typeof b.name === 'string' && b.name.trim() ? { name: b.name.trim().slice(0, 120) } : {}),
                ...(typeof b.initBonus === 'number' && Number.isInteger(b.initBonus) ? { initBonus: b.initBonus } : {}), ...(b.ask === true ? { ask: true } : {}),
            } : null;
        case 'encounter.effects': {
            if (!isUuid(b.id) || !Array.isArray(b.effects) || b.effects.length > ENCOUNTER_LIMITS.effects)
                return null;
            const effects = [];
            for (const e of b.effects) {
                const p = parseEffect(e);
                if (!p)
                    return null;
                effects.push(p);
            }
            if (new Set(effects.map((e) => e.id)).size !== effects.length)
                return null;
            return { kind: 'encounter.effects', id: b.id, effects };
        }
        case 'encounter.standing': {
            if (!isUuid(b.token) || !Array.isArray(b.effects) || b.effects.length > ENCOUNTER_LIMITS.effects)
                return null;
            const effects = [];
            for (const e of b.effects) {
                const p = parseEffect(e);
                if (!p)
                    return null;
                effects.push(p);
            }
            return { kind: 'encounter.standing', token: b.token, effects };
        }
        case 'encounter.action': {
            if (!isUuid(b.combatant) || typeof b.action !== 'string' || !ACTION_KEY_RE.test(b.action) || typeof b.spend !== 'boolean')
                return null;
            const out = { kind: 'encounter.action', combatant: b.combatant, action: b.action, spend: b.spend };
            if (b.spent === undefined && b.round === undefined && b.level === undefined)
                return out;
            const spent = parseSpent(b.spent);
            if (!spent || !isInt(b.round) || b.round < 1 || !RULE_CHECKS.includes(b.level))
                return null;
            if (b.moveLeft !== undefined && !isMoveLeft(b.moveLeft))
                return null;
            if (b.moveOf !== undefined && !isMoveKey(b.moveOf))
                return null;
            if ((b.perRound !== undefined && b.perRound !== true) || (b.noProvoke !== undefined && b.noProvoke !== true) || (b.attacksLeft !== undefined && !(isInt(b.attacksLeft) && b.attacksLeft >= 0 && b.attacksLeft <= 20)))
                return null;
            return { ...out, round: b.round, spent, level: b.level, ...(b.moveLeft !== undefined ? { moveLeft: b.moveLeft, ...(isMoveKey(b.moveOf) ? { moveOf: b.moveOf } : {}) } : {}), ...(b.perRound ? { perRound: true } : {}), ...(b.noProvoke ? { noProvoke: true } : {}), ...(b.attacksLeft !== undefined ? { attacksLeft: b.attacksLeft } : {}) };
        }
        case 'encounter.pool': {
            if (!isUuid(b.id) || typeof b.key !== 'string' || !POOL_KEY_RE.test(b.key))
                return null;
            if (b.max !== null && !isPoolSize(b.max))
                return null;
            if (b.left !== undefined && (b.max === null || !isPoolSize(b.left) || b.left > b.max))
                return null;
            if (b.refresh !== undefined && b.refresh !== 'round' && b.refresh !== 'dm' && b.refresh !== 'own_turn')
                return null;
            return { kind: 'encounter.pool', id: b.id, key: b.key, max: b.max, ...(b.left !== undefined ? { left: b.left } : {}), ...(b.refresh ? { refresh: b.refresh } : {}) };
        }
        case 'encounter.spend':
            return isUuid(b.id) && typeof b.key === 'string' && POOL_KEY_RE.test(b.key) && isPoolSize(b.dice) && b.dice >= 1 && isUuid(b.roll)
                && (b.burn === undefined || b.burn === true)
                ? { kind: 'encounter.spend', id: b.id, key: b.key, dice: b.dice, roll: b.roll, ...(b.burn ? { burn: true } : {}) } : null;
        case 'encounter.refresh':
            return { kind: 'encounter.refresh' };
        case 'encounter.fired':
            return isUuid(b.id) && isTurnKey(b.turn) && isFiredPoints(b.points) && isUuid(b.roll) ? { kind: 'encounter.fired', id: b.id, turn: b.turn, points: b.points, roll: b.roll } : null;
        case 'encounter.wounds': {
            if (b.wounds === undefined)
                return { kind: 'encounter.wounds' };
            const w = b.wounds;
            if (!Array.isArray(w) || w.length < 1 || w.length > ENCOUNTER_LIMITS.combatants)
                return null;
            const out = [];
            for (const x of w) {
                const r = x;
                if (!r || !isUuid(r.id) || !isInt(r.modifier) || Math.abs(r.modifier) > ENCOUNTER_LIMITS.scoreModifier)
                    return null;
                out.push({ id: r.id, modifier: r.modifier });
            }
            return new Set(out.map((x) => x.id)).size === out.length ? { kind: 'encounter.wounds', wounds: out } : null;
        }
        case 'encounter.priority':
            return Array.isArray(b.labels) && b.labels.length <= ENCOUNTER_LIMITS.priority && b.labels.every(isLabel)
                ? { kind: 'encounter.priority', labels: b.labels.map((l) => l.trim()) } : null;
        default:
            return null;
    }
}
/** A stamped sort rule (issues 1303, 1525): its order, whether the modifier breaks ties, and the first pass's score. */
/** A stamped sort (qa-runs 405): a rule and the roll-off's places, both well formed. */
function parseStampedSort(v) {
    const o = v;
    if (typeof o !== 'object' || o === null)
        return null;
    const rule = parseSortRule(o.rule);
    const ties = parseTies(o.ties);
    return rule && ties ? { rule, ties } : null;
}
function parseSortRule(v) {
    const r = v;
    if (typeof r !== 'object' || r === null || (r.order !== 'highest' && r.order !== 'lowest') || typeof r.modifier !== 'boolean')
        return null;
    if (r.firstPass !== undefined && r.firstPass !== true)
        return null;
    return { order: r.order, modifier: r.modifier, ...(r.firstPass ? { firstPass: true } : {}) };
}
/** A roll-off's places (issue 1123): a place per combatant id. */
function parseTies(v) {
    if (typeof v !== 'object' || v === null || Array.isArray(v))
        return null;
    const entries = Object.entries(v);
    if (entries.length > ENCOUNTER_LIMITS.combatants || !entries.every(([id, n]) => isUuid(id) && isInt(n) && n >= 0 && n < ENCOUNTER_LIMITS.combatants))
        return null;
    return Object.fromEntries(entries);
}
/**
 * The throws of an initiative record (`encounter.rolled`, a round's `reroll`): each total within the dice's range — on
 * a rated count (issue 1525) each its faces, every one a face of the die, summing to the total. `needDice`: a round's
 * throw always names its dice; a roll stored without them was one d20.
 */
function parseThrows(b, needDice = false) {
    if (!Array.isArray(b.results) || b.results.length > ENCOUNTER_LIMITS.combatants || (b.results.length < 1 && !needDice))
        return null;
    // The dice as stamped, kept, exploding or counted ones included (issue 1312); none stamped, the legacy d20.
    const dice = b.dice === undefined ? null : parseTableDice(b.dice);
    if ((b.dice !== undefined || needDice) && !dice)
        return null;
    if (b.rated !== undefined && (b.rated !== true || !dice || !isPlainDice(dice) || dice.count !== 1))
        return null;
    const rated = b.rated === true;
    const thrown = dice ?? LEGACY_INITIATIVE_DICE;
    const range = diceRange(thrown);
    const results = [];
    for (const r of b.results) {
        const rec = (r ?? {});
        const { id } = rec;
        const total = 'total' in rec ? rec.total : legacyRolledTotal(rec);
        if (!isUuid(id) || !isInt(total))
            return null;
        if (rated) {
            const f = rec.faces;
            if (!Array.isArray(f) || f.length < 1 || f.length > ENCOUNTER_LIMITS.initDice || !f.every((x) => isInt(x) && x >= 1 && x <= thrown.sides) || f.reduce((a, x) => a + x, 0) !== total)
                return null;
            results.push({ id, total, faces: [...f] });
        }
        else {
            if (rec.faces !== undefined || total < range.min || total > range.max)
                return null;
            results.push({ id, total });
        }
    }
    if (new Set(results.map((r) => r.id)).size !== results.length && needDice)
        return null;
    return { results, dice, rated };
}
const ACTION_KEY_RE = /^[a-z][a-z0-9_]{0,62}$/;
const POOL_KEY_RE = /^[a-z][a-z0-9_]{0,62}$/;
const isPoolSize = (v) => typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= POOL_LIMITS.max;
/** A combatant's pools from a snapshot or the log: each well formed, at most eight; anything else is left out. */
export function parseCombatantPools(v) {
    if (typeof v !== 'object' || v === null || Array.isArray(v))
        return null;
    const out = {};
    for (const [k, p] of Object.entries(v).slice(0, 8)) {
        const r = p;
        if (!POOL_KEY_RE.test(k) || typeof r !== 'object' || r === null || !isPoolSize(r.max) || !isPoolSize(r.left) || r.left > r.max || (r.refresh !== 'round' && r.refresh !== 'dm' && r.refresh !== 'own_turn'))
            continue;
        out[k] = { left: r.left, max: r.max, refresh: r.refresh };
    }
    return Object.keys(out).length ? out : null;
}
/** Every pool of this refresh full again; a combatant with none is returned as it was. */
export function refreshPools(combatants, refresh) {
    return combatants.map((c) => {
        if (!c.pools || !Object.values(c.pools).some((p) => p.refresh === refresh && p.left !== p.max))
            return c;
        return { ...c, pools: Object.fromEntries(Object.entries(c.pools).map(([k, p]) => [k, p.refresh === refresh ? { ...p, left: p.max } : p])) };
    });
}
/** The most actions one turn records: past it a spend is refused as a bad request, never kept. */
export const MAX_TURN_ACTIONS = 60;
function parseSpent(v) {
    if (!Array.isArray(v) || v.length > MAX_TURN_ACTIONS)
        return null;
    const out = [];
    for (const s of v) {
        const r = s;
        if (typeof r !== 'object' || r === null || typeof r.kind !== 'string' || !ACTION_KEY_RE.test(r.kind) || typeof r.over !== 'boolean')
            return null;
        out.push({ kind: r.kind, over: r.over });
    }
    return out;
}
function parseTurn(v) {
    const t = v;
    if (typeof t !== 'object' || t === null || !isUuid(t.combatant) || !isInt(t.round) || t.round < 1)
        return null;
    const spent = parseSpent(t.spent);
    if (t.moveLeft !== undefined && !isMoveLeft(t.moveLeft))
        return null;
    if (t.moveOf !== undefined && !isMoveKey(t.moveOf))
        return null;
    if (t.moved !== undefined && t.moved !== true)
        return null;
    return spent && RULE_CHECKS.includes(t.level) ? { combatant: t.combatant, round: t.round, spent, level: t.level, ...(t.moveLeft !== undefined ? { moveLeft: t.moveLeft, ...(isMoveKey(t.moveOf) ? { moveOf: t.moveOf } : {}) } : {}), ...(t.moved === true ? { moved: true } : {}), ...(t.diagonals === 1 ? { diagonals: 1 } : {}), ...(t.noProvoke === true ? { noProvoke: true } : {}), ...(isInt(t.attacksLeft) && t.attacksLeft >= 0 ? { attacksLeft: t.attacksLeft } : {}) } : null;
}
/** What is left of a Move (qa-runs#1161): squares, 0 to a bound no speed reaches. */
const isMoveLeft = (v) => typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 100_000;
/** A movement kind's key, as `object.move` names one. */
const isMoveKey = (v) => typeof v === 'string' && /^[a-z][a-z0-9_]{0,62}$/.test(v);
/**
 * A move of what is left of a Move, or the Move itself, stamped with what remains (qa-runs#1161): the active
 * combatant's turn record takes it, made when the turn has none yet. A token not acting now changes nothing.
 */
export function withMoveLeft(enc, tokenId, left, diagonals, moveOf) {
    const active = activeCombatant(enc);
    if (!enc.running || !active || !active.tokens.includes(tokenId))
        return enc;
    const turn = enc.turn && enc.turn.combatant === active.id && enc.turn.round === enc.round ? enc.turn : { combatant: active.id, round: enc.round, spent: [], level: 'quiet' };
    const { moveOf: _was, ...kept } = turn;
    return { ...enc, turn: { ...kept, spent: turn.spent.map((s) => ({ ...s })), moveLeft: Math.max(0, left), ...(moveOf ? { moveOf } : {}), moved: true, ...(diagonals !== undefined ? { diagonals: diagonals % 2 } : {}) } };
}
/** A fight's passes from a snapshot (issue 1525): the step, the pass, who has acted and the rule; else none. */
function parsePasses(v) {
    const p = v;
    if (typeof p !== 'object' || p === null || !isInt(p.step) || p.step < 1 || p.step > 100 || !isInt(p.pass) || p.pass < 1 || p.pass > MAX_PASSES)
        return null;
    const rule = parseSortRule(p.rule);
    if (!rule || !Array.isArray(p.acted) || !p.acted.every(isUuid))
        return null;
    return { step: p.step, pass: p.pass, acted: [...new Set(p.acted)], rule };
}
/** An encounter from an older snapshot, or none, read with today's shape. */
export function normaliseEncounter(e) {
    const combatants = Array.isArray(e?.combatants) ? e.combatants.map((c) => ({
        id: c.id, name: c.name ?? '', initBonus: Number(c.initBonus) || 0, initRoll: typeof c.initRoll === 'number' ? c.initRoll : null,
        effects: Array.isArray(c.effects) ? c.effects.map((x) => {
            const mods = x.mods === undefined ? null : parseEffectMods(x.mods);
            const old = x;
            const duration = old.duration ? parseDuration(old.duration)
                : typeof old.rounds === 'number' && old.rounds > 0 ? { amount: old.rounds, unit: { ...LEGACY_ROUND }, round: { ...LEGACY_ROUND } } : null;
            const by = x.rule === undefined ? null : parseEffectRule(x.rule);
            const then = x.second === undefined ? null : parsePoisonSecond(x.second);
            const again = x.retry === undefined ? null : parseEffectRetry(x.retry);
            const grown = x.grow === undefined ? null : parseGrow(x.grow);
            return { id: x.id, label: x.label, duration, good: x.good !== false, ...(grown ? { grow: grown } : {}), ...(x.keen === true ? { keen: true } : {}), ...(isEffectIcon(x.icon) ? { icon: x.icon } : {}), ...(typeof x.images === 'number' ? { images: x.images } : {}), ...(typeof x.temp === 'number' ? { temp: x.temp } : {}), ...(then ? { second: then } : {}), ...(again ? { retry: again } : {}), ...(mods && mods.length ? { mods } : {}), ...(x.until === 'next_turn' ? { until: 'next_turn' } : {}), ...(x.once ? { once: { until: x.once.until } } : {}), ...(typeof x.turns === 'number' && x.turns >= 1 ? { turns: x.turns } : {}), ...(x.endsOnAttack === true ? { endsOnAttack: true } : {}), ...(typeof x.miss === 'number' ? { miss: x.miss } : {}), ...(x.ward ? { ward: { ...x.ward, bypass: [...x.ward.bypass] } } : {}), ...(by ? { rule: by } : {}) };
        }) : [],
        tokens: Array.isArray(c.tokens) ? [...c.tokens] : [], shown: c.shown !== false,
        ...(typeof c.tie === 'number' ? { tie: c.tie } : {}),
        ...((() => { const p = c.pools === undefined ? null : parseCombatantPools(c.pools); return p ? { pools: p } : {}; })()),
        ...(typeof c.initDice === 'number' && Number.isInteger(c.initDice) && c.initDice >= 1 && c.initDice <= ENCOUNTER_LIMITS.initDice ? { initDice: c.initDice } : {}),
        ...(Array.isArray(c.initFaces) && c.initFaces.length >= 1 && c.initFaces.length <= ENCOUNTER_LIMITS.initDice && c.initFaces.every((x) => Number.isInteger(x) && x >= 1) ? { initFaces: [...c.initFaces] } : {}),
        ...(typeof c.scoreModifier === 'number' && Number.isInteger(c.scoreModifier) && c.scoreModifier !== 0 && Math.abs(c.scoreModifier) <= ENCOUNTER_LIMITS.scoreModifier ? { scoreModifier: c.scoreModifier } : {}),
        ...(typeof c.woundModifier === 'number' && Number.isInteger(c.woundModifier) && c.woundModifier !== 0 && Math.abs(c.woundModifier) <= ENCOUNTER_LIMITS.scoreModifier ? { woundModifier: c.woundModifier } : {}),
        ...(c.fired && isTurnKey(c.fired.turn) && isFiredPoints(c.fired.points) ? { fired: { turn: c.fired.turn, points: c.fired.points } } : {}),
    })) : [];
    const activeIndex = typeof e?.activeIndex === 'number' && e.activeIndex >= 0 && e.activeIndex < combatants.length ? e.activeIndex : 0;
    const turn = e?.turn ? parseTurn(e.turn) : null;
    const passes = e?.passes ? parsePasses(e.passes) : null;
    return { running: e?.running === true, round: typeof e?.round === 'number' && e.round >= 1 ? e.round : 1, activeIndex, combatants, priority: Array.isArray(e?.priority) ? [...e.priority] : [], ...(turn ? { turn } : {}),
        ...(typeof e?.fights === 'number' && Number.isInteger(e.fights) && e.fights > 0 ? { fights: e.fights } : {}), ...(passes ? { passes } : {}),
        ...((() => { const asked = Array.isArray(e?.asked) ? [...new Set(e.asked.filter((id) => combatants.some((c) => c.id === id)))] : []; return asked.length ? { asked } : {}; })()),
        ...((() => { const w = normaliseWaiting(e?.waiting, combatants); return w ? { waiting: w } : {}; })()),
        ...((() => { const h = normaliseHeld(e?.held, combatants); return h ? { held: h } : {}; })()),
        ...((() => { const r = Array.isArray(e?.resume) ? e.resume.map(parseTurn).filter((t) => t !== null) : []; return r.length ? { resume: r } : {}; })()),
        ...((() => { const o = normaliseOpportunities(e?.opportunities); return o ? { opportunities: o } : {}; })()),
        ...((() => { const r = Object.entries(e?.roundSpent ?? {}).flatMap(([id, l]) => { const s = parseSpent(l); return isUuid(id) && s?.length ? [[id, s]] : []; }); return r.length ? { roundSpent: Object.fromEntries(r) } : {}; })()) };
}
/** The turn's actions are the turn's: once another combatant acts, the round changes or the fight ends, they are gone. */
function keepTurn(enc) {
    if (!enc.turn)
        return enc;
    const active = activeCombatant(enc);
    if (active && active.id === enc.turn.combatant && enc.round === enc.turn.round)
        return enc;
    const { turn: _gone, ...rest } = enc;
    return rest;
}
/** Apply an accepted encounter event: pure. A missing combatant is a no-op, so a projected stream still reduces cleanly. */
export function reduceEncounter(enc, body) {
    return keepTurn(answerAsked(freshPools(enc, freshRound(enc, freshOpportunities(enc, reduceOne(enc, body), body), body), body)));
}
/**
 * A creature's count of attacks of opportunity goes as its turn starts, and every count with the fight (qa-runs#366). A
 * turn a readied action broke in on, coming back, starts nothing; nor does the DM's step back to a turn already started.
 */
function freshOpportunities(before, after, body) {
    if (!after.opportunities)
        return after;
    const { opportunities, ...rest } = after;
    if (!after.running)
        return rest;
    const was = activeCombatant(before);
    const now = activeCombatant(after);
    const moved = (was?.id ?? null) !== (now?.id ?? null) || before.round !== after.round;
    const resumed = !!now && after.turn?.combatant === now.id && after.turn.round === after.round;
    if (!now || !moved || resumed || body.kind === 'encounter.back')
        return after;
    // A casting of the turn that ended is over; one that takes the round lasts until its caster's turn comes.
    const ended = was?.tokens ?? [];
    const left = Object.fromEntries(Object.entries(opportunities).filter(([id]) => !now.tokens.includes(id)).map(([id, t]) => {
        if (!t.casting || t.casting.long || !ended.includes(id))
            return [id, t];
        const { casting: _over, ...kept } = t;
        return [id, kept];
    }));
    return Object.keys(left).length ? { ...rest, opportunities: left } : rest;
}
/** A combatant's `own_turn` pools are full again as its turn starts; a turn coming back starts nothing, as below. */
function freshPools(before, after, body) {
    const now = activeCombatant(after);
    if (!after.running || !now?.pools || body.kind === 'encounter.back' || !Object.values(now.pools).some((p) => p.refresh === 'own_turn' && p.left !== p.max))
        return after;
    const moved = (activeCombatant(before)?.id ?? null) !== now.id || before.round !== after.round;
    if (!moved || (after.turn?.combatant === now.id && after.turn.round === after.round))
        return after;
    return { ...after, combatants: after.combatants.map((c) => (c.id !== now.id ? c : { ...c, pools: Object.fromEntries(Object.entries(c.pools ?? {}).map(([k, p]) => [k, p.refresh === 'own_turn' ? { ...p, left: p.max } : p])) })) };
}
/** A combatant's per-round spends are back as its turn starts, and every one with the fight; a turn coming back starts nothing, as above. */
function freshRound(before, after, body) {
    if (!after.roundSpent)
        return after;
    const { roundSpent, ...rest } = after;
    if (!after.running)
        return rest;
    const was = activeCombatant(before);
    const now = activeCombatant(after);
    const moved = (was?.id ?? null) !== (now?.id ?? null) || before.round !== after.round;
    const resumed = !!now && after.turn?.combatant === now.id && after.turn.round === after.round;
    if (!now || !moved || resumed || body.kind === 'encounter.back')
        return after;
    const left = Object.fromEntries(Object.entries(roundSpent).filter(([id]) => id !== now.id));
    return Object.keys(left).length ? { ...rest, roundSpent: left } : rest;
}
/**
 * The DM's call (qa-runs 405) once anything has changed: a prompt goes as its combatant has a roll, however it got one
 * (a player's own, the DM's for them, Roll all, a round's throw, a card), or leaves the tracker.
 */
function answerAsked(enc) {
    if (!enc.asked)
        return enc;
    const asked = enc.asked.filter((id) => { const c = enc.combatants.find((x) => x.id === id); return !!c && typeof c.initRoll !== 'number'; });
    if (asked.length === enc.asked.length && asked.length)
        return enc;
    const { asked: _gone, ...rest } = enc;
    return asked.length ? { ...rest, asked } : rest;
}
/** The combatants the DM's call still waits for (qa-runs 405), in the tracker's order: *Waiting for: Shelby, Thorga*. */
export function waitingFor(enc) {
    const asked = new Set(enc.asked ?? []);
    return enc.combatants.filter((c) => asked.has(c.id) && typeof c.initRoll !== 'number');
}
function reduceOne(enc, body) {
    const at = (id) => enc.combatants.findIndex((c) => c.id === id);
    const patch = (id, f) => {
        const i = at(id);
        if (i < 0)
            return enc;
        const combatants = [...enc.combatants];
        combatants[i] = f(combatants[i]);
        return { ...enc, combatants };
    };
    switch (body.kind) {
        case 'encounter.add': {
            let combatants = [...enc.combatants];
            for (const c of body.combatants) {
                if (combatants.some((x) => x.id === c.id))
                    continue;
                const { name, renames } = nameForAdded(c.name, combatants);
                for (const r of renames)
                    combatants = combatants.map((x) => (x.id === r.id ? { ...x, name: r.name } : x));
                // A token acts for one combatant at a time: linking it here takes it from any other.
                combatants = combatants.map((x) => (x.tokens.some((t) => c.tokens.includes(t)) ? { ...x, tokens: x.tokens.filter((t) => !c.tokens.includes(t)) } : x));
                combatants.push({ id: c.id, name, initBonus: c.initBonus, initRoll: null, effects: [], tokens: [...c.tokens], shown: c.shown });
            }
            // Added to a running fight (qa-runs 1192): each player's combatant joins the call, prompted as Roll monsters prompts,
            // and its answer sorts it into the order as any answer does.
            const ask = (body.ask ?? []).filter((id) => !enc.combatants.some((x) => x.id === id) && !(enc.asked ?? []).includes(id));
            return ask.length ? { ...enc, combatants, asked: [...(enc.asked ?? []), ...ask] } : { ...enc, combatants };
        }
        case 'encounter.remove': {
            const active = enc.combatants[enc.activeIndex];
            const combatants = enc.combatants.filter((c) => !body.ids.includes(c.id));
            // The pointer stays on whoever it pointed at; if they left, on whoever now holds the slot (or the first).
            let activeIndex = active ? combatants.indexOf(active) : -1;
            // By passes (issue 1525): who acted leaves the list with them, and a phase that left goes to the next not yet taken.
            const ps = enc.passes;
            const passes = ps ? { ...ps, acted: ps.acted.filter((id) => !body.ids.includes(id)) } : undefined;
            if (activeIndex < 0 && passes)
                activeIndex = combatants.findIndex((c) => !passes.acted.includes(c.id) && hasPhase(c, passes.pass, passes.step));
            if (activeIndex < 0)
                activeIndex = Math.min(enc.activeIndex, Math.max(0, combatants.length - 1));
            return { ...enc, combatants, activeIndex, ...(passes ? { passes } : {}) };
        }
        case 'encounter.set': {
            const set = patch(body.id, (c) => {
                // A typed roll has no faces (issue 1525); a number of dice null is not known; a change of 0 is none.
                const { initFaces, initDice, scoreModifier, ...rest } = c;
                const dice = body.initDice === undefined ? initDice : body.initDice ?? undefined;
                const mod = body.scoreModifier === undefined ? scoreModifier : body.scoreModifier || undefined;
                return {
                    ...rest,
                    ...(body.name !== undefined ? { name: body.name } : {}),
                    ...(body.initBonus !== undefined ? { initBonus: body.initBonus } : {}),
                    ...(body.initRoll !== undefined ? { initRoll: body.initRoll } : {}),
                    ...(body.shown !== undefined ? { shown: body.shown } : {}),
                    ...(dice !== undefined ? { initDice: dice } : {}),
                    ...(initFaces && body.initRoll === undefined ? { initFaces } : {}),
                    ...(mod !== undefined ? { scoreModifier: mod } : {}),
                };
            });
            // A typed roll answering the DM's call (qa-runs 405): sorted as the server stamped it.
            if (body.sort)
                return sortByInitiative(set, body.sort.rule, body.sort.ties);
            // A score changed in a fight by passes (issue 1525): the phases not yet taken re-sort at once, the pointer staying.
            return set.passes && set.running && (body.initRoll !== undefined || body.scoreModifier !== undefined) ? sortByInitiative(set, set.passes.rule) : set;
        }
        case 'encounter.roll':
            return enc; // a request; the record is what applies
        case 'encounter.rolled': {
            // Roll monsters (qa-runs 405): the players' prompts open as the monsters' throws land; a call replaces the last.
            // The prompts of combatants rolled by this throw go as `answerAsked` sees their rolls.
            // A fresh call: each asked combatant's roll from before (an earlier fight's) is cleared, so it waits for its new one.
            const ask = body.ask?.filter((id) => at(id) >= 0 && !body.results.some((r) => r.id === id));
            const cleared = ask ? { ...enc, asked: ask, combatants: enc.combatants.map((c) => { if (!ask.includes(c.id))
                    return c; const { initFaces: _f, tie: _t, ...rest } = c; return { ...rest, initRoll: null }; }) } : enc;
            const thrown = applyThrows(cleared, body.results);
            return body.sort ? sortByInitiative(thrown, body.sort.rule, body.sort.ties) : thrown;
        }
        case 'encounter.sort': return sortByInitiative(enc, body.rule ?? LEGACY_SORT, body.ties);
        case 'encounter.deal': return enc; // a request; the record is what applies
        case 'encounter.dealt': return orderByCards(enc, dealOrder(body.cards));
        case 'encounter.move': {
            const i = at(body.id);
            return i < 0 ? enc : moveCombatant(enc, i, body.delta);
        }
        case 'encounter.link': {
            if (at(body.id) < 0)
                return enc;
            const combatants = enc.combatants.map((c) => (c.id === body.id
                ? { ...c, tokens: [...body.tokens], ...(body.name !== undefined ? { name: body.name } : {}), ...(body.initBonus !== undefined ? { initBonus: body.initBonus } : {}) }
                : c.tokens.some((t) => body.tokens.includes(t)) ? { ...c, tokens: c.tokens.filter((t) => !body.tokens.includes(t)) } : c));
            return body.ask && !(enc.asked ?? []).includes(body.id) ? { ...enc, combatants, asked: [...(enc.asked ?? []), body.id] } : { ...enc, combatants };
        }
        case 'encounter.effects': return patch(body.id, (c) => ({ ...c, effects: body.effects.map((e) => ({ ...e })) }));
        case 'encounter.standing': {
            const { [body.token]: _was, ...others } = enc.standing ?? {};
            const standing = body.effects.length ? { ...others, [body.token]: body.effects.map((e) => ({ ...e })) } : others;
            const { standing: _old, ...rest } = enc;
            return Object.keys(standing).length ? { ...rest, standing } : rest;
        }
        case 'encounter.priority': return { ...enc, priority: [...body.labels] };
        // A fight's start is its first combatant's turn start, and its end ends what lasts until a next turn (bug check of
        // issue 1508): an at-use choice never outlives the fight it was made in, nor carries into the next one.
        case 'encounter.start': {
            const { passes: _old, ...rest } = enc;
            const started = { ...rest, running: true, round: 1, activeIndex: 0, fights: (enc.fights ?? 0) + 1, combatants: refreshPools(enc.combatants, 'round') };
            if (!body.passes || !body.rule)
                return endTurnEffects(started);
            // By passes (issue 1525): the first phase is the first in the order with one (a score at 0 or less has none).
            const passes = { step: body.passes.step, pass: 1, acted: [], rule: body.rule };
            return endTurnEffects({ ...started, passes, activeIndex: Math.max(0, started.combatants.findIndex((c) => hasPhase(c, 1, passes.step))) });
        }
        case 'encounter.end': {
            // The fight is over: so is any call for initiative still open in it (qa-runs 405).
            const { passes: _gone, asked: _asked, ...rest } = enc;
            return { ...rest, running: false, combatants: enc.combatants.map((c) => (c.effects.some((e) => e.until === 'next_turn' || e.turns || e.once) ? { ...c, effects: c.effects.filter((e) => e.until !== 'next_turn' && !e.turns) } : c)) };
        }
        case 'encounter.next': return resumeTurn(advanceTurn(enc, body.reroll, body.dead));
        case 'encounter.back': return regressTurn(enc);
        case 'encounter.action': {
            // The request alone changes nothing; the stamped record sets the turn's list, for the turn it was taken in.
            if (!body.spent || !body.level || body.round === undefined)
                return enc;
            if (body.perRound) {
                if (!enc.running || enc.round !== body.round)
                    return enc;
                const { [body.combatant]: _was, ...others } = enc.roundSpent ?? {};
                const roundSpent = body.spent.length ? { ...others, [body.combatant]: body.spent.map((s) => ({ ...s })) } : others;
                const { roundSpent: _old, ...rest } = enc;
                return Object.keys(roundSpent).length ? { ...rest, roundSpent } : rest;
            }
            const active = activeCombatant(enc);
            if (!active || active.id !== body.combatant || enc.round !== body.round)
                return enc;
            // What is left of a Move stays only with the move action that Move spent (the server says so); any other action ends it.
            // Moving this turn (slice B) stays with the turn whatever action follows: no 5-foot step after a move.
            const moved = enc.turn?.moved === true && enc.turn.combatant === body.combatant && enc.turn.round === body.round;
            const attacksLeft = body.attacksLeft ?? (enc.turn?.combatant === body.combatant && enc.turn.round === body.round ? enc.turn.attacksLeft : undefined);
            // The diagonals walked this turn (qa-runs#1433) carry through every action of it: a second Move goes on at 10 ft.
            const diagonals = enc.turn?.combatant === body.combatant && enc.turn.round === body.round ? enc.turn.diagonals : undefined;
            return { ...enc, turn: { combatant: body.combatant, round: body.round, spent: body.spent.map((s) => ({ ...s })), level: body.level, ...(body.moveLeft !== undefined ? { moveLeft: body.moveLeft, ...(body.moveOf ? { moveOf: body.moveOf } : {}) } : {}), ...(moved ? { moved: true } : {}), ...(diagonals !== undefined ? { diagonals } : {}), ...(body.noProvoke ? { noProvoke: true } : {}), ...(attacksLeft !== undefined ? { attacksLeft } : {}) } };
        }
        case 'encounter.pool': {
            // The request alone changes nothing; the stamped record sets the pool.
            if (!body.refresh)
                return enc;
            const refresh = body.refresh;
            return patch(body.id, (c) => {
                const { pools: had = {}, ...rest } = c;
                const pools = { ...had };
                if (body.max === null)
                    delete pools[body.key];
                else {
                    const was = pools[body.key];
                    // A size set alone keeps what is left within it; a pool that was full, or new, is full at its new size.
                    const left = body.left !== undefined ? Math.min(body.left, body.max) : !was || was.left >= was.max ? body.max : Math.min(was.left, body.max);
                    pools[body.key] = { left, max: body.max, refresh };
                }
                return Object.keys(pools).length ? { ...rest, pools } : rest;
            });
        }
        case 'encounter.spend':
            return patch(body.id, (c) => {
                const p = c.pools?.[body.key];
                return p ? { ...c, pools: { ...c.pools, [body.key]: { ...p, left: Math.max(0, p.left - body.dice), ...(body.burn ? { max: Math.max(0, p.max - body.dice) } : {}) } } } : c;
            });
        case 'encounter.refresh': return { ...enc, combatants: refreshPools(enc.combatants, 'dm') };
        case 'encounter.fired': return patch(body.id, (c) => ({ ...c, fired: { turn: body.turn, points: body.points } }));
        case 'encounter.delay':
        case 'encounter.act':
        case 'encounter.waitLost': return reduceDelay(enc, body);
        case 'encounter.ready':
        case 'encounter.fire':
        case 'encounter.readyLost': return reduceReady(enc, body);
        case 'encounter.opportunity': return enc.running && enc.combatants.some((c) => c.tokens.includes(body.id)) ? reduceOpportunity(enc, body) : enc;
        case 'encounter.wounds': {
            if (!body.wounds)
                return enc;
            const by = new Map(body.wounds.map((w) => [w.id, w.modifier]));
            const combatants = enc.combatants.map((c) => {
                if (!by.has(c.id))
                    return c;
                const { woundModifier: _was, ...rest } = c;
                const m = by.get(c.id);
                return m ? { ...rest, woundModifier: m } : rest;
            });
            const next = { ...enc, combatants };
            // A score changed in a fight by passes (§5.3): the phases not yet taken re-sort at once, the pointer staying.
            return next.passes && next.running ? sortByInitiative(next, next.passes.rule) : next;
        }
        default: return enc;
    }
}
function patchIn(enc, id, f) {
    const i = enc.combatants.findIndex((c) => c.id === id);
    if (i < 0)
        return enc;
    const combatants = [...enc.combatants];
    combatants[i] = f(combatants[i]);
    return { ...enc, combatants };
}
// ── Who sees what, and who moves what ──────────────────────────────────────
export const activeCombatant = (enc) => (enc.running ? enc.combatants[enc.activeIndex] ?? null : null);
/** The combatant a token acts for, if any. */
export const combatantOf = (enc, tokenId) => enc.combatants.find((c) => c.tokens.includes(tokenId)) ?? null;
/** The linked tokens still on the board. */
export const tokensOf = (c, state) => c.tokens.map((id) => state.objects[id]).filter((o) => Boolean(o));
/**
 * Whether a viewer has a stone for a combatant (design/16 §4): the DM always;
 * a player when a linked token is one they can see, or, for a combatant with
 * nothing on the board, when the DM has marked it shown.
 */
export function combatantSeen(c, state, viewer) {
    if (viewer.role === 'dm')
        return true;
    const present = tokensOf(c, state);
    if (present.length === 0)
        return c.shown;
    return present.some((o) => canSee(o, viewer, scopeOf(state)));
}
/**
 * The movement lock (design/20 §2): during a fight a player moves a token
 * only if it acts for whoever's turn it is; a token in no combatant's list
 * moves for no player until the fight ends. Ownership is the caller's
 * check, made at the moment of the move; the DM is never restricted.
 */
export function moveRefusal(enc, tokenId, role) {
    if (role === 'dm' || !enc.running)
        return null;
    const c = combatantOf(enc, tokenId);
    if (!c)
        return NOT_IN_FIGHT;
    return c === activeCombatant(enc) ? null : NOT_YOUR_TURN;
}
/**
 * The same rule over a player's reading, for the browser's preview (design/20
 * §4.1: a refused move never starts). The server's `moveRefusal` over the
 * whole order is the authority; this only lets the screen say no first.
 */
export function previewRefusal(enc, tokenId, role) {
    if (role === 'dm' || !enc.running)
        return null;
    const c = enc.combatants.find((x) => x.tokens.includes(tokenId));
    if (!c)
        return NOT_IN_FIGHT;
    if (enc.active === c.id)
        return null;
    const active = enc.combatants.find((x) => x.id === enc.active);
    return active?.mine && active.name ? turnOfOwnToken(active.name) : NOT_YOUR_TURN;
}
/** Whether this person may end the current turn: the DM, or a player owning a token that acts now (design/16 §5). */
export function mayEndTurn(enc, state, viewer) {
    if (!enc.running)
        return false;
    if (viewer.role === 'dm')
        return true;
    const active = activeCombatant(enc);
    // Their token must be one they can see: one hidden from them, or not yet shown, is not theirs to end a turn for.
    return active !== null && tokensOf(active, state).some((o) => o.owners.includes(viewer.id) && canSee(o, viewer, null));
}
/** The turn's list as its owner and the DM read it: over shown only when the level warns (issue 1131). */
export const turnView = (t) => ({
    combatant: t.combatant, spent: t.spent.map((s) => ({ kind: s.kind, over: t.level !== 'quiet' && s.over })), ...(t.moveLeft !== undefined ? { moveLeft: t.moveLeft, ...(t.moveOf ? { moveOf: t.moveOf } : {}) } : {}), ...(t.moved ? { moved: true } : {}),
    ...(t.diagonals !== undefined ? { diagonals: t.diagonals } : {}),
});
/** The record that governs a combatant's numbers for a player: any visible linked token sharing a field shares it; an owner sees all; nothing on the board reads as a monster's defaults. */
function sharesFor(c, state, viewer) {
    const seen = tokensOf(c, state).filter((o) => canSee(o, viewer, scopeOf(state)));
    if (seen.length === 0)
        return { all: false, shares: { ...MONSTER_SHARES } };
    if (seen.some((o) => o.owners.includes(viewer.id)))
        return { all: true, shares: { ...MONSTER_SHARES } };
    // A player character's name always shows (COMBAT-007, nameShown): its record governs only its numbers.
    const record = (o) => (o.pc ? { ...o.shares, name: true } : o.shares);
    const shares = { ...record(seen[0]) };
    for (const o of seen.slice(1))
        for (const k of Object.keys(shares))
            shares[k] = shares[k] || record(o)[k];
    return { all: false, shares };
}
export function projectEncounter(state, viewer) {
    const enc = state.encounter;
    if (viewer.role === 'dm')
        return { ...enc, combatants: enc.combatants.map((c) => ({ ...c, effects: c.effects.map((e) => ({ ...e })), tokens: [...c.tokens], ...(c.pools ? { pools: Object.fromEntries(Object.entries(c.pools).map(([k, p]) => [k, { ...p }])) } : {}), ...(c.initFaces ? { initFaces: [...c.initFaces] } : {}), ...(c.fired ? { fired: { ...c.fired } } : {}) })), priority: [...enc.priority], ...(enc.turn ? { turn: { ...enc.turn, spent: enc.turn.spent.map((s) => ({ ...s })) } } : {}), ...(enc.passes ? { passes: { ...enc.passes, acted: [...enc.passes.acted], rule: { ...enc.passes.rule } } } : {}), ...(enc.asked ? { asked: [...enc.asked] } : {}), ...(enc.waiting ? { waiting: enc.waiting.map((w) => ({ ...w })) } : {}), ...(enc.held ? { held: enc.held.map((h) => ({ ...h })) } : {}) };
    const combatants = [];
    for (const c of enc.combatants) {
        if (!combatantSeen(c, state, viewer))
            continue;
        const { all, shares } = sharesFor(c, state, viewer);
        const seen = tokensOf(c, state).filter((o) => canSee(o, viewer, scopeOf(state)));
        const slice = { id: c.id, tokens: seen.map((o) => o.id), mine: seen.some((o) => o.owners.includes(viewer.id)) };
        if (all || shares.name)
            slice.name = c.name;
        if (all || shares.initRoll)
            slice.initRoll = c.initRoll;
        if (all || shares.initBonus)
            slice.initBonus = c.initBonus;
        // An effect's numbers (issue 1126) are its owner's and the DM's: another player sees the effect, never what it adds.
        // An effect a rule put on (issue 1508: Power Attack) is its owner's and the DM's alone: the choice behind it is theirs.
        if (all || shares.conditions)
            slice.effects = c.effects.filter((e) => all || !e.rule).map(({ mods, extra, once: _once, miss: _miss, ward: _ward, temp: _temp, second: _second, retry: _retry, images, ...e }) => ({ ...e, ...(all && images ? { images } : {}), ...(all && mods ? { mods: mods.map((m) => ({ ...m })) } : {}), ...(all && extra ? { extra: { ...extra } } : {}) }));
        // Its pools (issue 1524) are its owner's and the DM's: another player is never sent them, whatever the token shares.
        if (slice.mine && c.pools)
            slice.pools = seenPools(c.pools);
        if (slice.mine && c.fired)
            slice.fired = { ...c.fired };
        // Its score's change goes with its initiative (issue 1525); its dice and their faces are its owner's alone.
        if ((all || shares.initRoll) && c.scoreModifier)
            slice.scoreModifier = c.scoreModifier;
        // Its wounds' change (issue 1526) says how hurt it is: only to whoever is shown its resources as well as its score.
        if ((all || (shares.initRoll && shares.resources)) && c.woundModifier)
            slice.woundModifier = c.woundModifier;
        if (all && c.initDice)
            slice.initDice = c.initDice;
        if (all && c.initFaces)
            slice.initFaces = [...c.initFaces];
        combatants.push(slice);
    }
    const active = activeCombatant(enc);
    const seenIds = new Set(combatants.map((c) => c.id));
    let leftmost = null;
    if (active) {
        // Back from the pointer to the last turn this viewer saw come, round the loop once.
        for (let k = 0; k < enc.combatants.length; k += 1) {
            const c = enc.combatants[(enc.activeIndex - k + enc.combatants.length) % enc.combatants.length];
            if (seenIds.has(c.id)) {
                leftmost = c.id;
                break;
            }
        }
    }
    const out = { running: enc.running, round: enc.round, active: active && seenIds.has(active.id) ? active.id : null, leftmost, combatants, priority: [...enc.priority] };
    // The turn's actions (issue 1131): only to a player a token of whose acts for the combatant now acting and is seen.
    if (enc.turn && active && out.active === active.id && combatants.find((c) => c.id === active.id)?.mine)
        out.turn = turnView(enc.turn);
    // The round's reactions: each to the players of its own combatant alone.
    const myRound = combatants.filter((c) => c.mine && enc.roundSpent?.[c.id]).map((c) => [c.id, enc.roundSpent[c.id].map((s) => ({ ...s }))]);
    if (myRound.length)
        out.roundSpent = Object.fromEntries(myRound);
    // The phases (issue 1525) of the combatants this viewer has a stone for: a hidden one's are absent, not masked.
    if (enc.passes)
        out.passes = { pass: enc.passes.pass, step: enc.passes.step, phases: phasesOf(enc).filter((p) => seenIds.has(p.id)) };
    // The DM's call (qa-runs 405): only this player's own combatants, through a token they see (hidden means absent).
    const prompt = waitingFor(enc).flatMap((c) => { const mine = combatants.find((x) => x.id === c.id); return mine?.mine ? [{ id: c.id, name: c.name }] : []; });
    if (prompt.length)
        out.prompt = prompt;
    const waiting = waitingSeen(enc.waiting, (id) => seenIds.has(id));
    if (waiting)
        out.waiting = waiting;
    const held = heldSeen(enc.held, (id) => combatants.some((c) => c.id === id && c.mine));
    if (held)
        out.held = held;
    return out;
}
const seenPools = (pools) => Object.fromEntries(Object.entries(pools).map(([k, p]) => [k, { left: p.left, max: p.max }]));
/** The DM's own reading of the projection, in the player shape the strip draws from: every combatant, active as it is. */
export function dmEncounterView(enc) {
    const active = activeCombatant(enc);
    return {
        running: enc.running, round: enc.round, active: active?.id ?? null, leftmost: active?.id ?? null,
        combatants: enc.combatants.map((c) => ({ id: c.id, name: c.name, initRoll: c.initRoll, initBonus: c.initBonus, effects: c.effects, tokens: [...c.tokens], mine: false, ...(c.pools ? { pools: seenPools(c.pools) } : {}), ...(c.scoreModifier ? { scoreModifier: c.scoreModifier } : {}), ...(c.woundModifier ? { woundModifier: c.woundModifier } : {}) })),
        priority: [...enc.priority],
        ...(enc.turn ? { turn: turnView(enc.turn) } : {}),
        ...(enc.passes ? { passes: { pass: enc.passes.pass, step: enc.passes.step, phases: phasesOf(enc) } } : {}),
    };
}
export function stripStones(enc, count = 6) {
    const out = [];
    if (!enc.running || enc.combatants.length === 0)
        return out;
    if (enc.passes)
        return phaseStones(enc, enc.passes, count);
    const start = enc.leftmost ? Math.max(0, enc.combatants.findIndex((c) => c.id === enc.leftmost)) : 0;
    const n = enc.combatants.length;
    for (let k = 0; k < Math.min(count, n); k += 1) {
        const i = start + k;
        if (i === n)
            out.push({ kind: 'end', round: enc.round });
        const c = enc.combatants[i % n];
        out.push({ kind: 'combatant', combatant: c, active: k === 0 && enc.active === c.id });
    }
    if (start + Math.min(count, n) >= n && !out.some((s) => s.kind === 'end'))
        out.push({ kind: 'end', round: enc.round });
    return out;
}
/**
 * The stones of a fight by passes (issue 1525): one per phase — the phase acting now when seen, else the last phase this
 * viewer saw taken (the strip sits still), then the phases after it, the End of Round marker after the round's last.
 */
function phaseStones(enc, ps, count) {
    const out = [];
    const phases = ps.phases.filter((p) => enc.combatants.some((c) => c.id === p.id));
    if (!phases.length)
        return [{ kind: 'end', round: enc.round }];
    const now = phases.findIndex((p) => !p.done);
    const lastDone = now < 0 ? phases.length - 1 : now - 1;
    const start = now >= 0 && phases[now].id === enc.active ? now : Math.max(0, lastDone);
    for (let i = start; i < phases.length && out.length < count; i += 1) {
        const p = phases[i];
        out.push({ kind: 'combatant', combatant: enc.combatants.find((c) => c.id === p.id), active: i === now && p.id === enc.active, pass: p.pass });
    }
    if (out.length < count || start + count >= phases.length)
        out.push({ kind: 'end', round: enc.round });
    return out;
}
/** Which effects a stone shows (design/16 §2): up to four, by the DM's priority list; none without a list; and how many were left off. */
export function stoneEffects(effects, priority, max = 4) {
    if (priority.length === 0 || effects.length === 0)
        return { shown: [], more: 0 };
    // Listed effects first, in the list's order; the rest after, in their own order.
    const rank = (e) => { const i = priority.findIndex((p) => p.toLowerCase() === e.label.toLowerCase()); return i < 0 ? priority.length : i; };
    const ranked = effects.map((e, i) => ({ e, i })).sort((a, b) => rank(a.e) - rank(b.e) || a.i - b.i).map((x) => x.e);
    const shown = ranked.slice(0, max);
    return { shown, more: effects.length - shown.length };
}
/** The sort rule out of the system's initiative rule: its order, and the modifier when it breaks ties before any roll-off. */
export function sortRuleOf(init) {
    const m = init.ties.indexOf('modifier');
    const r = init.ties.indexOf('roll_off');
    const f = init.ties.indexOf('first_pass');
    // The first pass's score (issue 1525) breaks a tie before the modifier wherever it is listed before the roll-off.
    return { order: init.order, modifier: m >= 0 && (r < 0 || m < r), ...(f >= 0 && (r < 0 || f < r) ? { firstPass: true } : {}) };
}
/** Each token's size its effects give now (qa-runs#1442), in or out of a fight: the newest such effect's, as sizes do not stack. */
export function grownTokens(enc) {
    const out = new Map();
    const add = (token, fx) => { for (const e of fx)
        if (e.grow)
            out.set(token, e.grow); };
    for (const c of enc.combatants)
        for (const tk of c.tokens)
            add(tk, c.effects);
    for (const [tk, fx] of Object.entries(enc.standing ?? {}))
        add(tk, fx);
    return out;
}
/**
 * The tokens whose size effects changed between two encounters, each with the squares it now takes: the newest size
 * effect's, or, with none left, its size before them. A token whose size effects did not change is left out, so a DM's
 * own resize stands.
 */
export function growShifts(before, after) {
    const was = grownTokens(before);
    const now = grownTokens(after);
    const out = [];
    for (const token of new Set([...was.keys(), ...now.keys()])) {
        const a = was.get(token);
        const b = now.get(token);
        if (a && b && a.to.w === b.to.w && a.to.h === b.to.h)
            continue;
        out.push({ token, size: { ...(b ? b.to : a.from) } });
    }
    return out;
}
//# sourceMappingURL=encounter.js.map